import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mono, Num } from '@/components/forge/primitives'
import { formatDateTime } from '@/lib/format'
import { formatMeasure } from '@/lib/units'
import { cn } from '@/lib/cn'

/**
 * The Workstation plot: several channels, two axes, one span.
 *
 * DEMO. Lives here rather than in `components/forge/` because it is specific to
 * this screen — dual axes, threshold rules, maintenance shading and anomaly
 * onsets are a workstation's vocabulary, not the product's. If a second caller
 * appears it moves.
 *
 * ── Why not `TrendChart` ──────────────────────────────────────────────────
 * That one is a single-axis comparison with a target corridor behind it: one
 * subject drawn heavy, the rest as context. Here every series is equally the
 * subject, they are in different units, and the whole point is the relationship
 * between two of them. Widening `TrendChart` to a second axis, per-series
 * thresholds and event markers would have left one component with two jobs.
 *
 * ── Three modes, all of them real ─────────────────────────────────────────
 * The design draws four unlabelled glyphs — `⊞ ∿ ↕ ≡` — with no handler behind
 * any of them. `overlay` is the dual-axis plot; `stacked` gives each series its
 * own strip at its own scale, which is the only honest way to read four units
 * at once; `normalised` maps every series onto 0–100% of its own range, which is
 * the only way to compare the SHAPE of a vibration trace against a temperature
 * one. Each has a label.
 *
 * ── Everything drawn is computed upstream ─────────────────────────────────
 * Domains, rules, correlations, event positions: all resolved in the handler and
 * arriving on the payload. This file positions them. That is what keeps the
 * plot, the series table and the statistics panel from being three opinions.
 */

/* The drawing surface. Module constants for the same reason `TrendChart` gives:
   as locals they would be fresh identities on every render that the geometry
   memo would have to list to be honest. */
const W = 1000
const H = 340
const PAD = { top: 22, end: 56, bottom: 26, start: 56 }

const strokeFor = (tone) => `var(--hue-${tone})`

/** The plotted values of a series — holes (`null`) left out. */
const defined = (values) => values.filter((value) => value != null)

/**
 * ── Two payloads: an index grid, or a time axis with holes ─────────────────
 * The demo handler resolves every series onto one shared grid, so a point's
 * index IS its position. The live sensor chart (`features/sensorCharts/plot.js`)
 * cannot: each tag reports on its own clock. It sends `times` (ms, one per
 * stamp) and `timeDomain`, and a series holds `null` wherever it has no reading
 * at a stamp another series does. With `times` absent everything below behaves
 * exactly as it did.
 *
 * ── `fill`: take the height the parent gives, as the anomaly trace does ────
 * By default the plot keeps the viewBox's 1000×340 aspect. In a full-screen
 * dialog that leaves bands of empty stage, so `fill` stretches the SVG to its
 * box instead (`preserveAspectRatio="none"`). That is safe here because every
 * stroke is `non-scaling-stroke` and every label, tick and readout is HTML
 * placed by percentage of the same box. The one shape that would distort is
 * the hover dot, so under `fill` it is drawn as a zero-length round-capped
 * line, which stays a circle at any stretch.
 */

export function WorkstationChart({ chart, visible, mode = 'overlay', fill = false }) {
  const { t } = useTranslation(['app', 'common'])
  const clipId = useId()
  const [cursor, setCursor] = useState(null)

  const series = useMemo(
    () => chart.series.filter((entry) => visible[entry.id] !== false),
    [chart.series, visible],
  )

  const plotW = W - PAD.start - PAD.end
  const plotH = H - PAD.top - PAD.bottom
  const count = chart.stamps.length

  const times = chart.times ?? null
  const [t0, t1] = chart.timeDomain ?? [times?.[0] ?? 0, times?.at(-1) ?? 1]
  const tRange = t1 - t0 || 1
  const x = times
    ? (index) => PAD.start + ((times[index] - t0) / tRange) * plotW
    : (index) => PAD.start + (index / Math.max(1, count - 1)) * plotW

  /** Nearest stamp to a pointer position in plot units. */
  const indexAt = (px) => {
    if (!times) return Math.round(((px - PAD.start) / plotW) * (count - 1))
    const at = t0 + ((px - PAD.start) / plotW) * tRange
    let lo = 0
    let hi = count - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (times[mid] < at) lo = mid + 1
      else hi = mid
    }
    return lo > 0 && at - times[lo - 1] < times[lo] - at ? lo - 1 : lo
  }

  /**
   * A scaler per series.
   *
   * `overlay` puts a series on its own axis' domain. `normalised` puts every
   * series on its own min–max, which is what makes the shapes comparable and is
   * why the axis labels change to per-cent in that mode. `stacked` gives each
   * one a horizontal band of the plot and its own domain inside it.
   */
  const { scalers, lanes } = useMemo(() => {
    const map = new Map()
    const laneList = []
    series.forEach((entry, index) => {
      if (mode === 'normalised') {
        const min = Math.min(...defined(entry.values))
        const max = Math.max(...defined(entry.values))
        const range = max - min || 1
        map.set(entry.id, (value) => PAD.top + (1 - (value - min) / range) * plotH)
        return
      }
      if (mode === 'stacked') {
        const laneH = plotH / series.length
        const top = PAD.top + index * laneH
        const min = Math.min(...defined(entry.values), entry.band?.[0] ?? Infinity)
        const max = Math.max(...defined(entry.values), entry.band?.[1] ?? -Infinity)
        const range = max - min || 1
        // 12% breathing room inside the lane, so adjacent traces never touch.
        map.set(entry.id, (value) => top + laneH * 0.12 + (1 - (value - min) / range) * laneH * 0.76)
        laneList.push({ id: entry.id, tone: entry.tone, min, max, top: top + laneH * 0.12, bottom: top + laneH * 0.88 })
        return
      }
      const axis = chart.axes[entry.axis] ?? chart.axes.left
      const [min, max] = axis.domain
      const range = max - min || 1
      map.set(entry.id, (value) => PAD.top + (1 - (value - min) / range) * plotH)
    })
    return { scalers: map, lanes: laneList }
  }, [series, mode, chart.axes, plotH])

  const pathFor = (entry) => {
    const y = scalers.get(entry.id)
    /* A hole is skipped, not drawn as zero: consecutive readings of one tag are
       joined across instants where only another tag reported. */
    const points = []
    entry.values.forEach((value, index) => {
      if (value == null) return
      points.push(`${points.length ? 'L' : 'M'}${x(index).toFixed(1)} ${y(value).toFixed(1)}`)
    })
    return points.join(' ')
  }

  /* Axis ticks, in every mode, each saying what its own mode's scale is:
     overlay — five across each axis' domain;
     normalised — 0–100% of each series' own range, so per cent and not a value;
     stacked — each lane's own max at its top and min at its bottom, in the
     lane's colour, because no single value scale spans two lanes. */
  const axisTicks = (axis) => {
    if (!axis || mode !== 'overlay') return []
    const [min, max] = axis.domain
    return Array.from({ length: 5 }, (_, index) => {
      const value = min + ((max - min) * index) / 4
      return { key: `${index}`, text: formatTick(value), y: PAD.top + (1 - index / 4) * plotH }
    })
  }

  const leftTicks =
    mode === 'normalised'
      ? Array.from({ length: 5 }, (_, index) => ({
          key: `${index}`,
          text: `${index * 25}%`,
          y: PAD.top + (1 - index / 4) * plotH,
        }))
      : mode === 'stacked'
        ? lanes.flatMap((lane) => [
            { key: `${lane.id}-max`, text: formatTick(lane.max), y: lane.top, tone: lane.tone },
            ...(lane.max === lane.min
              ? []
              : [{ key: `${lane.id}-min`, text: formatTick(lane.min), y: lane.bottom, tone: lane.tone }]),
          ])
        : axisTicks(chart.axes.left)
  const rightTicks = axisTicks(chart.axes.right)

  /* Time labels: five, taken from the stamps the handler generated, so the axis
     always describes the span actually plotted. The prototype prints "May 20 …
     Jun 6" under every timeframe pill. */
  const timeTicks = times
    ? Array.from({ length: 5 }, (_, index) => ({
        index,
        x: PAD.start + (index / 4) * plotW,
        label: new Date(t0 + (index / 4) * tRange).toISOString(),
      }))
    : Array.from({ length: 5 }, (_, index) => {
        const at = Math.round((index / 4) * (count - 1))
        return { index: at, x: x(at), label: chart.stamps[at] }
      })

  /* The axis label follows the span. Five full "Aug 16, 2026, 3:04 PM" stamps do
     not fit under a chart and do not need to: over a day the date is a constant,
     and over a month the minute is noise. */
  const tickFormat =
    chart.span.hours <= 0.25
      ? { hour: 'numeric', minute: '2-digit', second: '2-digit' }
      : chart.span.hours <= 48
        ? { hour: 'numeric', minute: '2-digit' }
        : { month: 'short', day: 'numeric' }

  const hovered = cursor == null ? null : cursor

  return (
    <figure className={cn('m-0', fill && 'flex h-full min-h-0 flex-col')}>
      <div className={cn('relative', fill && 'min-h-[12rem] flex-1 basis-0')}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio={fill ? 'none' : undefined}
        className={cn('block w-full touch-none', fill ? 'absolute inset-0 h-full' : 'h-auto')}
        role="img"
        aria-label={t('sensors.workstation.chartLabel', {
          count: series.length,
          name: chart.name,
        })}
        onPointerLeave={() => setCursor(null)}
        onPointerMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect()
          const px = ((event.clientX - box.left) / box.width) * W
          if (!count) return
          const index = indexAt(px)
          setCursor(Math.min(count - 1, Math.max(0, index)))
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={PAD.start} y={PAD.top} width={plotW} height={plotH} />
          </clipPath>
        </defs>

        {/* Grid — four lines, subtle. Never a cage. */}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
          <line
            key={fraction}
            x1={PAD.start}
            x2={W - PAD.end}
            y1={PAD.top + plotH * fraction}
            y2={PAD.top + plotH * fraction}
            stroke="var(--border-subtle)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Maintenance windows, shaded. Drawn first, so a trace crossing one is
            read over it rather than hidden behind it. */}
        <g clipPath={`url(#${clipId})`}>
          {chart.events.maintenance.map((event) => (
            <rect
              key={`${event.assetId}-${event.at}`}
              x={PAD.start + event.from * plotW}
              y={PAD.top}
              width={Math.max(3, (event.to - event.from) * plotW)}
              height={plotH}
              fill="var(--ink-mute)"
              opacity="0.1"
            />
          ))}

          {/* Threshold rules, one per visible series, on that series' own scale.
              Absolute rules make no sense on a normalised or stacked plot, so
              they are drawn only where they would mean something. */}
          {mode === 'overlay' &&
            chart.rules
              .filter((rule) => visible[rule.seriesId] !== false)
              .map((rule) => {
                const y = scalers.get(rule.seriesId)
                if (!y) return null
                return (
                  <line
                    key={rule.seriesId}
                    x1={PAD.start}
                    x2={W - PAD.end}
                    y1={y(rule.value)}
                    y2={y(rule.value)}
                    stroke={strokeFor(rule.tone)}
                    strokeWidth="1"
                    strokeDasharray="6 4"
                    opacity="0.55"
                    vectorEffect="non-scaling-stroke"
                  />
                )
              })}

          {/* The traces. Reverse order so the first series — the chart's subject
              — is painted last and reads on top. */}
          {[...series].reverse().map((entry) => (
            <path
              key={entry.id}
              d={pathFor(entry)}
              fill="none"
              stroke={strokeFor(entry.tone)}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </g>

        {/* Anomaly onsets. Positioned from the alert's own detection time, so a
            marker sits where the record says the reading went wrong. */}
        {chart.events.anomalies
          .filter((event) => visible[event.channelId] !== false)
          .map((event) => (
            <g key={event.alertId}>
              <line
                x1={PAD.start + event.ratio * plotW}
                x2={PAD.start + event.ratio * plotW}
                y1={PAD.top}
                y2={PAD.top + plotH}
                stroke="var(--hue-red)"
                strokeWidth="1"
                strokeDasharray="3 3"
                opacity="0.4"
                vectorEffect="non-scaling-stroke"
              />
              <polygon
                points={`${PAD.start + event.ratio * plotW - 5},${PAD.top - 9} ${PAD.start + event.ratio * plotW + 5},${PAD.top - 9} ${PAD.start + event.ratio * plotW},${PAD.top - 1}`}
                fill="var(--hue-red)"
              />
            </g>
          ))}

        {/* Crosshair. */}
        {hovered != null ? (
          <line
            x1={x(hovered)}
            x2={x(hovered)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            stroke="var(--ink-mute)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        {hovered != null &&
          series.map((entry) => {
            const y = scalers.get(entry.id)
            if (entry.values[hovered] == null) return null
            if (fill) {
              const cy = y(entry.values[hovered])
              return (
                <g key={entry.id}>
                  <line
                    x1={x(hovered)}
                    x2={x(hovered)}
                    y1={cy}
                    y2={cy}
                    stroke={strokeFor(entry.tone)}
                    strokeWidth="9"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <line
                    x1={x(hovered)}
                    x2={x(hovered)}
                    y1={cy}
                    y2={cy}
                    stroke="var(--surface-card)"
                    strokeWidth="5"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              )
            }
            return (
              <circle
                key={entry.id}
                cx={x(hovered)}
                cy={y(entry.values[hovered])}
                r="3.5"
                fill="var(--surface-card)"
                stroke={strokeFor(entry.tone)}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            )
          })}

      </svg>

      {/* ── Axis tick labels, as HTML ─────────────────────────────────────
          NOT `<text>` inside the SVG. The viewBox is 1000 wide and scaled to the
          card, so `fontSize="11"` in user units rendered at about 9.7px here
          while the time labels directly beneath it — real HTML — were 11px. Two
          sizes on the two axes of one chart. Positioned by percentage off the
          same geometry the grid uses. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        {leftTicks.map((tick) => (
          <span
            key={`l${tick.key}`}
            className="num absolute -translate-y-1/2 text-end text-micro font-normal text-ink-mute"
            style={{
              top: `${(tick.y / H) * 100}%`,
              insetInlineStart: 0,
              width: `${((PAD.start - 8) / W) * 100}%`,
              ...(tick.tone ? { color: strokeFor(tick.tone) } : {}),
            }}
          >
            {tick.text}
          </span>
        ))}
        {rightTicks.map((tick) => (
          <span
            key={`r${tick.key}`}
            className="num absolute -translate-y-1/2 text-start text-micro font-normal text-ink-mute"
            style={{
              top: `${(tick.y / H) * 100}%`,
              insetInlineEnd: 0,
              width: `${((PAD.end - 8) / W) * 100}%`,
              paddingInlineStart: `${(8 / W) * 100}%`,
            }}
          >
            {tick.text}
          </span>
        ))}
      </div>

      {hovered != null ? (
        <HoverReadout
          leftPct={(x(hovered) / W) * 100}
          stamp={chart.stamps[hovered]}
          rows={series
            .filter((entry) => entry.values[hovered] != null)
            .map((entry) => ({
              id: entry.id,
              tone: entry.tone,
              name: entry.name,
              value: formatMeasure({ value: entry.values[hovered], unit: entry.unit }),
            }))}
        />
      ) : null}
      </div>

      {/* Axis units and the time axis, in HTML rather than SVG so they take the
          product's own type ramp instead of a hardcoded font size. */}
      <div className="mt-1 flex items-baseline justify-between text-micro font-normal text-ink-mute">
        <Mono>
          {mode === 'overlay'
            ? chart.axes.left.label
            : t(`sensors.workstation.mode.${mode}Axis`)}
        </Mono>
        {mode === 'overlay' && chart.axes.right ? <Mono>{chart.axes.right.label}</Mono> : null}
      </div>
      {/* Each stamp placed under the x it names, off the same geometry as the
          plot — the ends align to the plot's edges so neither hangs outside. */}
      <div aria-hidden="true" className="relative mt-1.5 h-4 text-micro font-normal text-ink-dim">
        {timeTicks.map((tick, position) => (
          <Num
            key={tick.index}
            className={cn(
              'absolute top-0 whitespace-nowrap',
              position === 0 ? '' : position === timeTicks.length - 1 ? '-translate-x-full' : '-translate-x-1/2',
            )}
            style={{ left: `${(tick.x / W) * 100}%` }}
          >
            {formatDateTime(tick.label, tickFormat)}
          </Num>
        ))}
      </div>

    </figure>
  )
}

/** Tick values: two decimals at most, and no trailing zeros. */
const formatTick = (value) => `${Math.round(value * 100) / 100}`

/**
 * The time and every series' value under the pointer, ON the plot — the same
 * arrangement as the anomaly trace's readout. HTML positioned by percentage,
 * centred on the crosshair and pinned to an edge near either end so it never
 * hangs outside the chart. `pointer-events-none`, or it would catch its own
 * hover and flicker.
 */
function HoverReadout({ leftPct, stamp, rows }) {
  const align = leftPct < 15 ? 'start' : leftPct > 85 ? 'end' : 'center'
  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute top-2 flex flex-col gap-1 rounded-sm border border-hairline bg-overlay px-2.5 py-1.5 whitespace-nowrap shadow-lift',
        align === 'center' && '-translate-x-1/2',
        align === 'end' && '-translate-x-full',
      )}
      style={{
        left: `${leftPct}%`,
        marginInlineStart: align === 'start' ? '0.5rem' : align === 'end' ? '-0.5rem' : undefined,
      }}
    >
      <Mono className="text-micro font-medium text-ink">{stamp ? formatDateTime(stamp, READOUT_STAMP) : '—'}</Mono>
      {rows.map((row) => (
        <span key={row.id} className="flex items-center gap-1.5">
          <span
            className="inline-block h-0.5 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: strokeFor(row.tone) }}
          />
          <span className="max-w-48 truncate text-micro text-ink-dim">{row.name}</span>
          <Num className="ms-auto ps-2 text-body-sm font-semibold text-ink">{row.value}</Num>
        </span>
      ))}
    </div>
  )
}

/* Seconds included: on a one-minute window every reading shares its minute. */
const READOUT_STAMP = { dateStyle: 'medium', timeStyle: 'medium' }
